/**
 * Editor chrome renderer for the CanvasKit editor.
 * Draws selection boxes, resize/rotate handles, snap guides, marquee, and hover outlines.
 * All chrome is drawn in screen coordinates for consistent visual size regardless of zoom.
 */
import type { Canvas, CanvasKit, Font, FontMgr } from 'canvaskit-wasm';
import type { SceneDocument } from '@/lib/scene-document';

import { parseColor , getCachedImage } from './canvaskit-render-scene';
import {
  GUIDE_DASH,
  GUIDE_COLOR,
  type SnapGuide,
  collectColumnGuides,
  DEFAULT_LAYOUT_GUIDE_COLOR,
  DEFAULT_LAYOUT_GUIDE_OPACITY,
} from './canvaskit-snap';
import { getNodeAABB, getSvgPathBounds, getSelectionAABB, getNodeWorldBounds , getParentWorldOffset, type Rect } from './canvaskit-hit-test';
import {
  HANDLE_SIZE,
  nodeCornerRadii,
  type WorldBounds,
  RADIUS_HANDLE_IDS,
  getEnabledHandles,
  getHandlePositions,
  supportsRadiusHandles,
  shouldShowRadiusHandles,
  getRadiusHandlePositions,
} from './canvaskit-transform';

// ─── Constants ────────────────────────────────────────────────────────────

const SELECTION_COLOR = '#2563EB'; // blue-600
const HOVER_COLOR = '#93C5FD';    // blue-300
const MARQUEE_FILL = 'rgba(37, 99, 235, 0.08)';
const MARQUEE_STROKE = '#2563EB';

// ─── Helpers ──────────────────────────────────────────────────────────────

function toScreen(
  sceneX: number,
  sceneY: number,
  zoom: number,
  panX: number,
  panY: number,
): { x: number; y: number } {
  return {
    x: sceneX * zoom + panX,
    y: sceneY * zoom + panY,
  };
}

// ─── Selection box ────────────────────────────────────────────────────────

export function drawSelectionBoxes(
  ck: CanvasKit,
  canvas: Canvas,
  doc: SceneDocument,
  selectedIds: Set<string>,
  zoom: number,
  panX: number,
  panY: number,
): void {
  if (selectedIds.size === 0) return;

  const paint = new ck.Paint();
  paint.setStyle(ck.PaintStyle.Stroke);
  paint.setColor(parseColor(ck, SELECTION_COLOR));
  paint.setStrokeWidth(1);
  paint.setAntiAlias(true);

  for (const nodeId of selectedIds) {
    const bounds = getNodeWorldBounds(doc, nodeId);
    drawRotatedRect(ck, canvas, paint, bounds, zoom, panX, panY);
  }

  paint.delete();
}

function drawRotatedRect(
  ck: CanvasKit,
  canvas: Canvas,
  paint: ReturnType<CanvasKit['Paint']['prototype']['copy']>,
  bounds: WorldBounds,
  zoom: number,
  panX: number,
  panY: number,
): void {
  const { x, y, width, height, rotation } = bounds;
  const screenPos = toScreen(x, y, zoom, panX, panY);
  const screenW = width * zoom;
  const screenH = height * zoom;

  canvas.save();
  canvas.translate(screenPos.x, screenPos.y);

  if (rotation) {
    canvas.translate(screenW / 2, screenH / 2);
    canvas.rotate(rotation, 0, 0);
    canvas.translate(-screenW / 2, -screenH / 2);
  }

  canvas.drawRect(ck.LTRBRect(0, 0, screenW, screenH), paint);
  canvas.restore();
}

// ─── Reorder drop indicator ───────────────────────────────────────────────

/** Minimal shape of the store's `dragReorder` state needed to draw the seam. */
export interface ReorderIndicator {
  parentId: string;
  layoutMode: 'VERTICAL' | 'HORIZONTAL';
  toIndex: number;
}

/**
 * Draw a Figma-style insertion line at the slot where the dragged section will
 * land. `doc` is the render doc (dragged already spliced into its target slot
 * by buildRenderDoc), so the seam is the gap between the dragged node's
 * laid-out neighbours. Drawn in screen space for a constant 3px thickness.
 */
export function drawReorderIndicator(
  ck: CanvasKit,
  canvas: Canvas,
  doc: SceneDocument,
  reorder: ReorderIndicator,
  zoom: number,
  panX: number,
  panY: number,
): void {
  const parent = doc.nodes[reorder.parentId];
  if (!parent?.children?.length) return;

  const isV = reorder.layoutMode === 'VERTICAL';
  const gap = parent.layoutGap ?? 0;
  const children = parent.children;
  const idx = Math.max(0, Math.min(reorder.toIndex, children.length - 1));

  // Neighbours bracketing the dragged slot (dragged itself sits at `idx`).
  const aboveId = children[idx - 1];
  const belowId = children[idx + 1];

  const parentAABB = getNodeAABB(doc, reorder.parentId);
  const padTop = parent.layoutPaddingTop ?? 0;
  const padBottom = parent.layoutPaddingBottom ?? 0;
  const padLeft = parent.layoutPaddingLeft ?? 0;
  const padRight = parent.layoutPaddingRight ?? 0;

  const paint = new ck.Paint();
  paint.setStyle(ck.PaintStyle.Fill);
  paint.setColor(parseColor(ck, SELECTION_COLOR));
  paint.setAntiAlias(true);

  const THICKNESS = 3;
  const CAP = 4; // radius of the rounded end dots

  if (isV) {
    let seamY: number;
    if (aboveId) {
      const a = getNodeAABB(doc, aboveId);
      seamY = a.y + a.height + gap / 2;
    } else if (belowId) {
      const b = getNodeAABB(doc, belowId);
      seamY = b.y - gap / 2;
    } else {
      seamY = parentAABB.y + padTop;
    }
    const left = parentAABB.x + padLeft;
    const right = parentAABB.x + parentAABB.width - padRight;
    const s1 = toScreen(left, seamY, zoom, panX, panY);
    const s2 = toScreen(right, seamY, zoom, panX, panY);
    canvas.drawRRect(
      ck.RRectXY(ck.LTRBRect(s1.x, s1.y - THICKNESS / 2, s2.x, s2.y + THICKNESS / 2), THICKNESS / 2, THICKNESS / 2),
      paint,
    );
    canvas.drawCircle(s1.x, s1.y, CAP, paint);
    canvas.drawCircle(s2.x, s2.y, CAP, paint);
  } else {
    let seamX: number;
    if (aboveId) {
      const a = getNodeAABB(doc, aboveId);
      seamX = a.x + a.width + gap / 2;
    } else if (belowId) {
      const b = getNodeAABB(doc, belowId);
      seamX = b.x - gap / 2;
    } else {
      seamX = parentAABB.x + padLeft;
    }
    const top = parentAABB.y + padTop;
    const bottom = parentAABB.y + parentAABB.height - padBottom;
    const s1 = toScreen(seamX, top, zoom, panX, panY);
    const s2 = toScreen(seamX, bottom, zoom, panX, panY);
    canvas.drawRRect(
      ck.RRectXY(ck.LTRBRect(s1.x - THICKNESS / 2, s1.y, s2.x + THICKNESS / 2, s2.y), THICKNESS / 2, THICKNESS / 2),
      paint,
    );
    canvas.drawCircle(s1.x, s1.y, CAP, paint);
    canvas.drawCircle(s2.x, s2.y, CAP, paint);
  }

  paint.delete();
}

// ─── Nest drop target ─────────────────────────────────────────────────────

/**
 * Highlight a FRAME/GROUP that the dragged node will nest into on release — a
 * thick rounded blue outline plus a faint blue wash, Figma-style. Drawn in
 * screen space over the target's world AABB.
 */
export function drawNestTargetHighlight(
  ck: CanvasKit,
  canvas: Canvas,
  doc: SceneDocument,
  targetId: string,
  zoom: number,
  panX: number,
  panY: number,
): void {
  const node = doc.nodes[targetId];
  if (!node) return;
  const aabb = getNodeAABB(doc, targetId);
  const p = toScreen(aabb.x, aabb.y, zoom, panX, panY);
  const w = aabb.width * zoom;
  const h = aabb.height * zoom;
  const rect = ck.RRectXY(ck.LTRBRect(p.x, p.y, p.x + w, p.y + h), 4, 4);

  const wash = new ck.Paint();
  wash.setStyle(ck.PaintStyle.Fill);
  wash.setColor(parseColor(ck, MARQUEE_FILL));
  wash.setAntiAlias(true);
  canvas.drawRRect(rect, wash);
  wash.delete();

  const stroke = new ck.Paint();
  stroke.setStyle(ck.PaintStyle.Stroke);
  stroke.setColor(parseColor(ck, SELECTION_COLOR));
  stroke.setStrokeWidth(2);
  stroke.setAntiAlias(true);
  canvas.drawRRect(rect, stroke);
  stroke.delete();
}

// ─── Resize / Rotate handles ──────────────────────────────────────────────

export function drawHandles(
  ck: CanvasKit,
  canvas: Canvas,
  doc: SceneDocument,
  selectedIds: Set<string>,
  zoom: number,
  panX: number,
  panY: number,
): void {
  // Only draw handles for single selection
  if (selectedIds.size !== 1) return;

  const nodeId = [...selectedIds][0];
  const node = doc.nodes[nodeId];
  // HUG axes / hugging artboards expose fewer (or no) handles — shared with
  // hit-testing in useEditorEvents so visibility and grabbability agree.
  const enabledHandles = getEnabledHandles(node);
  if (enabledHandles.length === 0) return;

  const bounds = getNodeWorldBounds(doc, nodeId);
  const handles = getHandlePositions(bounds);

  const halfSize = HANDLE_SIZE / 2;

  const linePaint = new ck.Paint();
  linePaint.setStyle(ck.PaintStyle.Stroke);
  linePaint.setColor(parseColor(ck, SELECTION_COLOR));
  linePaint.setStrokeWidth(1);
  linePaint.setAntiAlias(true);

  if (enabledHandles.includes('rotate')) {
    // Draw rotation handle connecting line
    const topCenter = handles.n;
    const rotHandle = handles.rotate;
    const screenTop = toScreen(topCenter.x, topCenter.y, zoom, panX, panY);
    const screenRot = toScreen(rotHandle.x, rotHandle.y, zoom, panX, panY);

    const lineBuilder = new ck.PathBuilder();
    lineBuilder.moveTo(screenTop.x, screenTop.y);
    lineBuilder.lineTo(screenRot.x, screenRot.y);
    const linePath = lineBuilder.detach();
    canvas.drawPath(linePath, linePaint);
    linePath.delete();

    // Draw rotation handle (circle)
    const rotFill = new ck.Paint();
    rotFill.setStyle(ck.PaintStyle.Fill);
    rotFill.setColor(ck.Color(255, 255, 255, 1));
    rotFill.setAntiAlias(true);
    canvas.drawCircle(screenRot.x, screenRot.y, halfSize, rotFill);
    canvas.drawCircle(screenRot.x, screenRot.y, halfSize, linePaint);
    rotFill.delete();
  }

  // Draw resize handles (white squares with blue border)
  const fillPaint = new ck.Paint();
  fillPaint.setStyle(ck.PaintStyle.Fill);
  fillPaint.setColor(ck.Color(255, 255, 255, 1));
  fillPaint.setAntiAlias(true);

  const strokePaint = new ck.Paint();
  strokePaint.setStyle(ck.PaintStyle.Stroke);
  strokePaint.setColor(parseColor(ck, SELECTION_COLOR));
  strokePaint.setStrokeWidth(1);
  strokePaint.setAntiAlias(true);

  const resizeHandles = enabledHandles.filter((id) => id !== 'rotate');
  for (const id of resizeHandles) {
    const pos = handles[id];
    const screen = toScreen(pos.x, pos.y, zoom, panX, panY);
    const rect = ck.LTRBRect(
      screen.x - halfSize,
      screen.y - halfSize,
      screen.x + halfSize,
      screen.y + halfSize,
    );
    canvas.drawRect(rect, fillPaint);
    canvas.drawRect(rect, strokePaint);
  }

  // Corner-radius handles: Figma-style circles inset on each corner diagonal,
  // tracking the current radius. Hidden on small-on-screen nodes so they don't
  // collide with the resize squares.
  if (supportsRadiusHandles(node) && shouldShowRadiusHandles(bounds, zoom)) {
    const radiusPositions = getRadiusHandlePositions(bounds, nodeCornerRadii(node), zoom);
    for (const id of RADIUS_HANDLE_IDS) {
      const pos = radiusPositions[id];
      const screen = toScreen(pos.x, pos.y, zoom, panX, panY);
      canvas.drawCircle(screen.x, screen.y, 4, fillPaint);
      canvas.drawCircle(screen.x, screen.y, 4, strokePaint);
    }
  }

  fillPaint.delete();
  strokePaint.delete();
  linePaint.delete();
}

// ─── Multi-select bounding box + handles ──────────────────────────────────

/**
 * Draw the combined selection box and its 8 resize handles when 2+ nodes are
 * selected. No rotate or corner-radius handles here — multi-select rotation is
 * deferred, and a group has no single radius. `doc` is the laid-out render doc,
 * so getSelectionAABB reports rendered positions.
 */
export function drawMultiSelectChrome(
  ck: CanvasKit,
  canvas: Canvas,
  doc: SceneDocument,
  selectedIds: Set<string>,
  zoom: number,
  panX: number,
  panY: number,
): void {
  if (selectedIds.size < 2) return;
  const aabb = getSelectionAABB(doc, selectedIds);
  if (!aabb) return;

  const topLeft = toScreen(aabb.x, aabb.y, zoom, panX, panY);
  const screenW = aabb.width * zoom;
  const screenH = aabb.height * zoom;

  // Group box (solid blue), matching the per-node selection stroke.
  const boxPaint = new ck.Paint();
  boxPaint.setStyle(ck.PaintStyle.Stroke);
  boxPaint.setColor(parseColor(ck, SELECTION_COLOR));
  boxPaint.setStrokeWidth(1);
  boxPaint.setAntiAlias(true);
  canvas.drawRect(
    ck.LTRBRect(topLeft.x, topLeft.y, topLeft.x + screenW, topLeft.y + screenH),
    boxPaint,
  );
  boxPaint.delete();

  const handles = getHandlePositions({ ...aabb, rotation: 0 });
  const halfSize = HANDLE_SIZE / 2;

  const fillPaint = new ck.Paint();
  fillPaint.setStyle(ck.PaintStyle.Fill);
  fillPaint.setColor(ck.Color(255, 255, 255, 1));
  fillPaint.setAntiAlias(true);

  const strokePaint = new ck.Paint();
  strokePaint.setStyle(ck.PaintStyle.Stroke);
  strokePaint.setColor(parseColor(ck, SELECTION_COLOR));
  strokePaint.setStrokeWidth(1);
  strokePaint.setAntiAlias(true);

  const resizeIds = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'] as const;
  for (const id of resizeIds) {
    const pos = handles[id];
    const screen = toScreen(pos.x, pos.y, zoom, panX, panY);
    const rect = ck.LTRBRect(
      screen.x - halfSize,
      screen.y - halfSize,
      screen.x + halfSize,
      screen.y + halfSize,
    );
    canvas.drawRect(rect, fillPaint);
    canvas.drawRect(rect, strokePaint);
  }

  fillPaint.delete();
  strokePaint.delete();
}

// ─── Alt-hover measurements ────────────────────────────────────────────────

const MEASURE_COLOR = '#F24822'; // Figma measurement red
const MEASURE_LABEL_SIZE = 11; // screen px, constant regardless of zoom
const MEASURE_TICK = 3; // half-length of the end ticks, screen px

/** Distance label: integer when whole, else 1 decimal. Always the magnitude. */
function formatMeasureDistance(v: number): string {
  const r = Math.round(Math.abs(v) * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

/** Red pill + white text centered on a screen-space point. */
function drawMeasureLabel(
  ck: CanvasKit,
  canvas: Canvas,
  font: Font,
  text: string,
  screenCx: number,
  screenCy: number,
): void {
  const glyphs = font.getGlyphIDs(text);
  const widths = font.getGlyphWidths(glyphs);
  let textW = 0;
  for (const w of widths) textW += w;

  const padX = 4;
  const padY = 3;
  const pillW = textW + padX * 2;
  const pillH = MEASURE_LABEL_SIZE + padY * 2;
  const left = screenCx - pillW / 2;
  const top = screenCy - pillH / 2;

  const bg = new ck.Paint();
  bg.setStyle(ck.PaintStyle.Fill);
  bg.setColor(parseColor(ck, MEASURE_COLOR));
  bg.setAntiAlias(true);
  canvas.drawRRect(ck.RRectXY(ck.LTRBRect(left, top, left + pillW, top + pillH), 2, 2), bg);
  bg.delete();

  const textPaint = new ck.Paint();
  textPaint.setStyle(ck.PaintStyle.Fill);
  textPaint.setColor(ck.Color(255, 255, 255, 1));
  textPaint.setAntiAlias(true);
  canvas.drawText(text, screenCx - textW / 2, screenCy + MEASURE_LABEL_SIZE * 0.35, textPaint, font);
  textPaint.delete();
}

/**
 * Figma-style Alt-hover measurement overlay: red gap lines + pixel labels
 * between the current selection box (`sel`) and a hovered target rect (`tgt`),
 * both in scene space. Disjoint axes get a single gap line each; when the two
 * rects overlap on both axes the four edge-to-edge offsets are drawn instead
 * (the padding/inset case). Lines and labels are screen-space so they stay a
 * constant size at any zoom; values are canvas px.
 */
export function drawMeasurements(
  ck: CanvasKit,
  canvas: Canvas,
  fontMgr: FontMgr,
  sel: Rect,
  tgt: Rect,
  zoom: number,
  panX: number,
  panY: number,
): void {
  const linePaint = new ck.Paint();
  linePaint.setStyle(ck.PaintStyle.Stroke);
  linePaint.setColor(parseColor(ck, MEASURE_COLOR));
  linePaint.setStrokeWidth(1);
  linePaint.setAntiAlias(true);

  const typeface = fontMgr.matchFamilyStyle('Roboto', {
    weight: ck.FontWeight.Medium,
    width: ck.FontWidth.Normal,
    slant: ck.FontSlant.Upright,
  });
  const font = new ck.Font(typeface ?? null, MEASURE_LABEL_SIZE);
  font.setEdging(ck.FontEdging.AntiAlias);

  const aL = sel.x;
  const aR = sel.x + sel.width;
  const aT = sel.y;
  const aB = sel.y + sel.height;
  const bL = tgt.x;
  const bR = tgt.x + tgt.width;
  const bT = tgt.y;
  const bB = tgt.y + tgt.height;

  // Horizontal red segment between two scene x's at a shared scene y.
  const drawH = (x1: number, x2: number, y: number, label: string) => {
    const s1 = toScreen(x1, y, zoom, panX, panY);
    const s2 = toScreen(x2, y, zoom, panX, panY);
    canvas.drawLine(s1.x, s1.y, s2.x, s2.y, linePaint);
    canvas.drawLine(s1.x, s1.y - MEASURE_TICK, s1.x, s1.y + MEASURE_TICK, linePaint);
    canvas.drawLine(s2.x, s2.y - MEASURE_TICK, s2.x, s2.y + MEASURE_TICK, linePaint);
    drawMeasureLabel(ck, canvas, font, label, (s1.x + s2.x) / 2, s1.y);
  };

  // Vertical red segment between two scene y's at a shared scene x.
  const drawV = (y1: number, y2: number, x: number, label: string) => {
    const s1 = toScreen(x, y1, zoom, panX, panY);
    const s2 = toScreen(x, y2, zoom, panX, panY);
    canvas.drawLine(s1.x, s1.y, s2.x, s2.y, linePaint);
    canvas.drawLine(s1.x - MEASURE_TICK, s1.y, s1.x + MEASURE_TICK, s1.y, linePaint);
    canvas.drawLine(s2.x - MEASURE_TICK, s2.y, s2.x + MEASURE_TICK, s2.y, linePaint);
    drawMeasureLabel(ck, canvas, font, label, s1.x, (s1.y + s2.y) / 2);
  };

  const gapRight = bL - aR; // >0: target sits to the right of the selection
  const gapLeft = aL - bR; // >0: target sits to the left
  const gapBelow = bT - aB; // >0: target sits below
  const gapAbove = aT - bB; // >0: target sits above
  const dx = Math.max(gapRight, gapLeft);
  const dy = Math.max(gapBelow, gapAbove);
  const EPS = 1e-3;

  if (dx > EPS || dy > EPS) {
    // Disjoint on at least one axis: draw the facing-edge gap on each axis
    // that has real separation (an L-pair when the target is diagonal).
    if (dx > EPS) {
      const rightOf = gapRight >= gapLeft;
      const x1 = rightOf ? aR : bR;
      const x2 = rightOf ? bL : aL;
      const ovT = Math.max(aT, bT);
      const ovB = Math.min(aB, bB);
      const y = ovB > ovT ? (ovT + ovB) / 2 : (aT + aB + bT + bB) / 4;
      drawH(x1, x2, y, formatMeasureDistance(dx));
    }
    if (dy > EPS) {
      const belowOf = gapBelow >= gapAbove;
      const y1 = belowOf ? aB : bB;
      const y2 = belowOf ? bT : aT;
      const ovL = Math.max(aL, bL);
      const ovR = Math.min(aR, bR);
      const x = ovR > ovL ? (ovL + ovR) / 2 : (aL + aR + bL + bR) / 4;
      drawV(y1, y2, x, formatMeasureDistance(dy));
    }
  } else {
    // Overlapping / nested: the four edge-to-edge offsets (Figma's inset
    // measurement when a node sits inside another).
    const midY = (Math.max(aT, bT) + Math.min(aB, bB)) / 2;
    const midX = (Math.max(aL, bL) + Math.min(aR, bR)) / 2;
    if (Math.abs(bL - aL) > EPS) drawH(aL, bL, midY, formatMeasureDistance(bL - aL));
    if (Math.abs(bR - aR) > EPS) drawH(aR, bR, midY, formatMeasureDistance(bR - aR));
    if (Math.abs(bT - aT) > EPS) drawV(aT, bT, midX, formatMeasureDistance(bT - aT));
    if (Math.abs(bB - aB) > EPS) drawV(aB, bB, midX, formatMeasureDistance(bB - aB));
  }

  font.delete();
  linePaint.delete();
}

// ─── Hover outline ────────────────────────────────────────────────────────

export function drawHoverOutline(
  ck: CanvasKit,
  canvas: Canvas,
  doc: SceneDocument,
  hoveredId: string,
  selectedIds: Set<string>,
  zoom: number,
  panX: number,
  panY: number,
): void {
  if (selectedIds.has(hoveredId)) return;

  const bounds = getNodeWorldBounds(doc, hoveredId);
  const paint = new ck.Paint();
  paint.setStyle(ck.PaintStyle.Stroke);
  paint.setColor(parseColor(ck, HOVER_COLOR));
  paint.setStrokeWidth(1);
  paint.setAntiAlias(true);

  drawRotatedRect(ck, canvas, paint, bounds, zoom, panX, panY);
  paint.delete();
}

// ─── Snap guides ──────────────────────────────────────────────────────────

export function drawSnapGuides(
  ck: CanvasKit,
  canvas: Canvas,
  guides: SnapGuide[],
  canvasWidth: number,
  canvasHeight: number,
  zoom: number,
  panX: number,
  panY: number,
): void {
  if (guides.length === 0) return;

  const paint = new ck.Paint();
  paint.setStyle(ck.PaintStyle.Stroke);
  paint.setColor(parseColor(ck, GUIDE_COLOR));
  paint.setStrokeWidth(1);
  paint.setAntiAlias(true);
  const effect = ck.PathEffect.MakeDash(GUIDE_DASH);
  paint.setPathEffect(effect);

  for (const guide of guides) {
    const builder = new ck.PathBuilder();
    if (guide.orientation === 'vertical') {
      const screen = toScreen(guide.position, 0, zoom, panX, panY);
      const screenBottom = toScreen(guide.position, canvasHeight, zoom, panX, panY);
      builder.moveTo(screen.x, screen.y);
      builder.lineTo(screenBottom.x, screenBottom.y);
    } else {
      const screen = toScreen(0, guide.position, zoom, panX, panY);
      const screenRight = toScreen(canvasWidth, guide.position, zoom, panX, panY);
      builder.moveTo(screen.x, screen.y);
      builder.lineTo(screenRight.x, screenRight.y);
    }
    const path = builder.detach();
    canvas.drawPath(path, paint);
    path.delete();
  }

  effect.delete();
  paint.delete();
}

// ─── Layout guides (column grids) ──────────────────────────────────────────

/**
 * Draw the column layout guides authored on each artboard frame — a translucent
 * fill per column band, Figma-red by default. Screen-space so the fill tracks
 * the artboard at any zoom. Caller draws this over the scene but under selection
 * chrome. `guidesVisible` is the session-level master toggle (Shift+G); each
 * config's own `visible` flag is honoured inside collectColumnGuides.
 */
export function drawLayoutGuides(
  ck: CanvasKit,
  canvas: Canvas,
  doc: SceneDocument,
  guidesVisible: boolean,
  zoom: number,
  panX: number,
  panY: number,
): void {
  if (!guidesVisible) return;
  const guides = collectColumnGuides(doc);
  if (guides.length === 0) return;

  const fill = new ck.Paint();
  fill.setStyle(ck.PaintStyle.Fill);
  fill.setAntiAlias(true);

  for (const { node, cols, bands } of guides) {
    const parsed = parseColor(ck, cols.color ?? DEFAULT_LAYOUT_GUIDE_COLOR);
    const opacity = cols.opacity ?? DEFAULT_LAYOUT_GUIDE_OPACITY;
    fill.setColor(ck.Color(parsed[0] * 255, parsed[1] * 255, parsed[2] * 255, opacity));
    const top = node.y * zoom + panY;
    const bottom = (node.y + node.height) * zoom + panY;
    for (const band of bands) {
      const left = band.x * zoom + panX;
      const right = (band.x + band.width) * zoom + panX;
      canvas.drawRect(ck.LTRBRect(left, top, right, bottom), fill);
    }
  }

  fill.delete();
}

// ─── Link zone overlays ──────────────────────────────────────────────────

import { getLinkedNodes, getLinkZoneGroup, LINK_ZONE_COLORS } from './canvaskit-mutations';

/**
 * Paint a single link highlight (semi-transparent fill + dashed border + corner
 * color tab) at the given screen-space rect. Shared by the link-zone overlay and
 * the per-node link overlay so the two stay visually identical. `rotation` is in
 * degrees; pass 0 for axis-aligned zones.
 */
function drawLinkRect(
  ck: CanvasKit,
  canvas: Canvas,
  screenX: number,
  screenY: number,
  screenW: number,
  screenH: number,
  rotation: number,
  color: string,
): void {
  canvas.save();
  canvas.translate(screenX, screenY);
  if (rotation) {
    canvas.translate(screenW / 2, screenH / 2);
    canvas.rotate(rotation, 0, 0);
    canvas.translate(-screenW / 2, -screenH / 2);
  }
  const rect = ck.LTRBRect(0, 0, screenW, screenH);

  // Semi-transparent fill
  const fillPaint = new ck.Paint();
  fillPaint.setStyle(ck.PaintStyle.Fill);
  const parsed = parseColor(ck, color);
  fillPaint.setColor(ck.Color(parsed[0] * 255, parsed[1] * 255, parsed[2] * 255, 0.25));
  fillPaint.setAntiAlias(true);
  canvas.drawRect(rect, fillPaint);
  fillPaint.delete();

  // Dashed border
  const strokePaint = new ck.Paint();
  strokePaint.setStyle(ck.PaintStyle.Stroke);
  strokePaint.setColor(parseColor(ck, color));
  strokePaint.setStrokeWidth(1.5);
  strokePaint.setAntiAlias(true);
  const dash = ck.PathEffect.MakeDash([4, 3]);
  strokePaint.setPathEffect(dash);
  canvas.drawRect(rect, strokePaint);
  dash.delete();
  strokePaint.delete();

  // Color label tab in corner
  if (screenW > 30 && screenH > 16) {
    const labelPaint = new ck.Paint();
    labelPaint.setStyle(ck.PaintStyle.Fill);
    labelPaint.setColor(parseColor(ck, color));
    labelPaint.setAntiAlias(true);
    canvas.drawRect(ck.LTRBRect(0, 0, Math.min(screenW, 50), 14), labelPaint);
    labelPaint.delete();
  }

  canvas.restore();
}

export function drawLinkZones(
  ck: CanvasKit,
  canvas: Canvas,
  doc: SceneDocument,
  zoom: number,
  panX: number,
  panY: number,
): void {
  const group = getLinkZoneGroup(doc);
  if (!group?.children?.length) return;

  for (let i = 0; i < group.children.length; i++) {
    const node = doc.nodes[group.children[i]];
    if (!node) continue;

    const color = LINK_ZONE_COLORS[i % LINK_ZONE_COLORS.length];

    // Zone coordinates are relative to the group (which is at 0,0)
    const worldX = node.x + (group.x ?? 0);
    const worldY = node.y + (group.y ?? 0);
    const screen = toScreen(worldX, worldY, zoom, panX, panY);
    drawLinkRect(ck, canvas, screen.x, screen.y, node.width * zoom, node.height * zoom, 0, color);
  }
}

/**
 * Highlight every node that carries a linkUrl (logo, hero, cards, button) — the
 * way agent-authored canvas emails make elements clickable. Mirrors the
 * link-zone overlay so the user can see what is clickable without opening each
 * layer. Uses world bounds so nested / rotated nodes line up.
 */
export function drawNodeLinkOverlays(
  ck: CanvasKit,
  canvas: Canvas,
  doc: SceneDocument,
  zoom: number,
  panX: number,
  panY: number,
): void {
  const linked = getLinkedNodes(doc);

  for (let i = 0; i < linked.length; i++) {
    const bounds = getNodeWorldBounds(doc, linked[i].id);
    if (bounds.width <= 0 || bounds.height <= 0) continue;

    const color = LINK_ZONE_COLORS[i % LINK_ZONE_COLORS.length];
    const screen = toScreen(bounds.x, bounds.y, zoom, panX, panY);
    drawLinkRect(
      ck,
      canvas,
      screen.x,
      screen.y,
      bounds.width * zoom,
      bounds.height * zoom,
      bounds.rotation,
      color,
    );
  }
}

// ─── Crop mode overlay ────────────────────────────────────────────────

import {
  resolveCropTarget,
  getCropHandlePositions,
  computeDefaultTransform,
  getFullImageBoundsInScene,
} from './canvaskit-crop';

import type { CropHandleId } from './canvaskit-crop';

export function drawCropOverlay(
  ck: CanvasKit,
  canvas: Canvas,
  doc: SceneDocument,
  croppingNodeId: string,
  zoom: number,
  panX: number,
  panY: number,
): void {
  const node = doc.nodes[croppingNodeId];
  // Crop targets are IMAGE nodes or a shape's IMAGE fill — same overlay either
  // way, just a different home for the transform.
  const target = resolveCropTarget(node);
  if (!node || !target) return;

  const cached = getCachedImage(target.src, Math.max(node.width, node.height));
  if (!cached) return;

  const imgW = cached.width();
  const imgH = cached.height();
  const transform =
    target.transform ??
    computeDefaultTransform(imgW, imgH, node.width, node.height, target.fit);

  // node.x / node.y are local to the parent. Resolve to scene-space so the
  // crop chrome lines up with the actual rendered image when nested in groups.
  const parentOffset = getParentWorldOffset(doc, croppingNodeId);
  const worldX = parentOffset.x + node.x;
  const worldY = parentOffset.y + node.y;

  // Where the full image sits in scene coords (image extends beyond the
  // frame in any direction where offsets are negative or where the scaled
  // image is larger than the frame in that axis).
  const fullBounds = getFullImageBoundsInScene(transform, worldX, worldY, imgW, imgH);

  // Screen-space rects for the dimmed full image and the framed (visible) area.
  const imgScreen = toScreen(fullBounds.x, fullBounds.y, zoom, panX, panY);
  const imgScreenW = fullBounds.width * zoom;
  const imgScreenH = fullBounds.height * zoom;

  const frameScreen = toScreen(worldX, worldY, zoom, panX, panY);
  const frameScreenW = node.width * zoom;
  const frameScreenH = node.height * zoom;

  const fullDstRect = ck.LTRBRect(imgScreen.x, imgScreen.y, imgScreen.x + imgScreenW, imgScreen.y + imgScreenH);
  const frameDstRect = ck.LTRBRect(frameScreen.x, frameScreen.y, frameScreen.x + frameScreenW, frameScreen.y + frameScreenH);

  // Dim the full image so the framed portion stands out.
  const dimPaint = new ck.Paint();
  dimPaint.setAntiAlias(true);
  dimPaint.setAlphaf(0.3);
  canvas.drawImageRect(cached, ck.LTRBRect(0, 0, imgW, imgH), fullDstRect, dimPaint);
  dimPaint.delete();

  // Dark wash on the dimmed image.
  const overlayPaint = new ck.Paint();
  overlayPaint.setStyle(ck.PaintStyle.Fill);
  overlayPaint.setColor(ck.Color(0, 0, 0, 0.4));
  overlayPaint.setAntiAlias(true);
  canvas.drawRect(fullDstRect, overlayPaint);
  overlayPaint.delete();

  // Redraw the visible portion of the image at full opacity inside the frame.
  // Clip to the frame rect, then draw the full image at the same scale/offset
  // that the scene renderer will use — so the crop overlay matches the live
  // render exactly.
  const cropPaint = new ck.Paint();
  cropPaint.setAntiAlias(true);
  canvas.save();
  canvas.clipRect(frameDstRect, ck.ClipOp.Intersect, true);
  canvas.drawImageRect(cached, ck.LTRBRect(0, 0, imgW, imgH), fullDstRect, cropPaint);
  canvas.restore();
  cropPaint.delete();

  const cropDstRect = frameDstRect;

  // Dashed outline around the frame (crop window)
  const framePaint = new ck.Paint();
  framePaint.setStyle(ck.PaintStyle.Stroke);
  framePaint.setColor(parseColor(ck, SELECTION_COLOR));
  framePaint.setStrokeWidth(2);
  framePaint.setAntiAlias(true);
  const dash = ck.PathEffect.MakeDash([6, 4]);
  framePaint.setPathEffect(dash);
  canvas.drawRect(cropDstRect, framePaint);
  dash.delete();
  framePaint.delete();

  // Draw crop resize handles (white squares with blue border)
  const cropHandles = getCropHandlePositions(worldX, worldY, node.width, node.height);
  const handleFill = new ck.Paint();
  handleFill.setStyle(ck.PaintStyle.Fill);
  handleFill.setColor(ck.Color(255, 255, 255, 1));
  handleFill.setAntiAlias(true);

  const handleStroke = new ck.Paint();
  handleStroke.setStyle(ck.PaintStyle.Stroke);
  handleStroke.setColor(parseColor(ck, SELECTION_COLOR));
  handleStroke.setStrokeWidth(1);
  handleStroke.setAntiAlias(true);

  const halfSize = HANDLE_SIZE / 2;
  const cropHandleIds: CropHandleId[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
  for (const id of cropHandleIds) {
    const pos = cropHandles[id];
    const screen = toScreen(pos.x, pos.y, zoom, panX, panY);
    const handleRect = ck.LTRBRect(
      screen.x - halfSize,
      screen.y - halfSize,
      screen.x + halfSize,
      screen.y + halfSize,
    );
    canvas.drawRect(handleRect, handleFill);
    canvas.drawRect(handleRect, handleStroke);
  }

  handleFill.delete();
  handleStroke.delete();
}

// ─── Vector edit overlay ──────────────────────────────────────────────────


import type { VectorPath } from './canvaskit-vector-points';

const VECTOR_ANCHOR_SIZE = 6;
const VECTOR_HANDLE_SIZE = 5;
const VECTOR_HANDLE_LINE_COLOR = '#999999';

export function drawVectorEditOverlay(
  ck: CanvasKit,
  canvas: Canvas,
  doc: SceneDocument,
  nodeId: string,
  vectorPaths: VectorPath[],
  selectedPoints: Set<string>,
  zoom: number,
  panX: number,
  panY: number,
): void {
  const node = doc.nodes[nodeId];
  if (!node?.pathData) return;

  const parentOffset = getParentWorldOffset(doc, nodeId);
  const worldX = parentOffset.x + node.x;
  const worldY = parentOffset.y + node.y;

  const pathBounds = getSvgPathBounds(node.pathData);
  const scaleX = pathBounds.width > 0 ? node.width / pathBounds.width : 1;
  const scaleY = pathBounds.height > 0 ? node.height / pathBounds.height : 1;

  // Convert a path-native point to screen coordinates
  function ptToScreen(px: number, py: number) {
    const sceneX = worldX + (px - pathBounds.x) * scaleX;
    const sceneY = worldY + (py - pathBounds.y) * scaleY;
    return toScreen(sceneX, sceneY, zoom, panX, panY);
  }

  // 1. Draw the full path outline
  const outlinePaint = new ck.Paint();
  outlinePaint.setStyle(ck.PaintStyle.Stroke);
  outlinePaint.setColor(parseColor(ck, SELECTION_COLOR));
  outlinePaint.setStrokeWidth(1);
  outlinePaint.setAntiAlias(true);

  for (const vp of vectorPaths) {
    if (vp.points.length < 2) continue;
    const builder = new ck.PathBuilder();
    const first = ptToScreen(vp.points[0].x, vp.points[0].y);
    builder.moveTo(first.x, first.y);

    const len = vp.closed ? vp.points.length : vp.points.length - 1;
    for (let i = 0; i < len; i++) {
      const curr = vp.points[i];
      const next = vp.points[(i + 1) % vp.points.length];
      const hasOut = curr.handleOut && (curr.handleOut.x !== 0 || curr.handleOut.y !== 0);
      const hasIn = next.handleIn && (next.handleIn.x !== 0 || next.handleIn.y !== 0);

      if (hasOut || hasIn) {
        const cp1 = ptToScreen(
          curr.x + (curr.handleOut?.x ?? 0),
          curr.y + (curr.handleOut?.y ?? 0),
        );
        const cp2 = ptToScreen(
          next.x + (next.handleIn?.x ?? 0),
          next.y + (next.handleIn?.y ?? 0),
        );
        const end = ptToScreen(next.x, next.y);
        builder.cubicTo(cp1.x, cp1.y, cp2.x, cp2.y, end.x, end.y);
      } else {
        const end = ptToScreen(next.x, next.y);
        builder.lineTo(end.x, end.y);
      }
    }

    if (vp.closed) builder.close();
    const path = builder.detach();
    canvas.drawPath(path, outlinePaint);
    path.delete();
  }
  outlinePaint.delete();

  // 2. Draw handle lines and handle circles
  const handleLinePaint = new ck.Paint();
  handleLinePaint.setStyle(ck.PaintStyle.Stroke);
  handleLinePaint.setColor(parseColor(ck, VECTOR_HANDLE_LINE_COLOR));
  handleLinePaint.setStrokeWidth(1);
  handleLinePaint.setAntiAlias(true);

  const handleFillPaint = new ck.Paint();
  handleFillPaint.setStyle(ck.PaintStyle.Fill);
  handleFillPaint.setColor(ck.Color(255, 255, 255, 1));
  handleFillPaint.setAntiAlias(true);

  const handleStrokePaint = new ck.Paint();
  handleStrokePaint.setStyle(ck.PaintStyle.Stroke);
  handleStrokePaint.setColor(parseColor(ck, SELECTION_COLOR));
  handleStrokePaint.setStrokeWidth(1);
  handleStrokePaint.setAntiAlias(true);

  for (let pi = 0; pi < vectorPaths.length; pi++) {
    const pts = vectorPaths[pi].points;
    for (let i = 0; i < pts.length; i++) {
      const pt = pts[i];
      const key = `${pi}:${i}`;
      const isSelected = selectedPoints.has(key);
      const anchor = ptToScreen(pt.x, pt.y);

      // Only show handles for selected points (like Figma)
      if (isSelected) {
        // handleIn line + circle
        if (pt.handleIn && (pt.handleIn.x !== 0 || pt.handleIn.y !== 0)) {
          const hScreen = ptToScreen(pt.x + pt.handleIn.x, pt.y + pt.handleIn.y);
          const lineBuilder = new ck.PathBuilder();
          lineBuilder.moveTo(anchor.x, anchor.y);
          lineBuilder.lineTo(hScreen.x, hScreen.y);
          const linePath = lineBuilder.detach();
          canvas.drawPath(linePath, handleLinePaint);
          linePath.delete();
          canvas.drawCircle(hScreen.x, hScreen.y, VECTOR_HANDLE_SIZE / 2, handleFillPaint);
          canvas.drawCircle(hScreen.x, hScreen.y, VECTOR_HANDLE_SIZE / 2, handleStrokePaint);
        }

        // handleOut line + circle
        if (pt.handleOut && (pt.handleOut.x !== 0 || pt.handleOut.y !== 0)) {
          const hScreen = ptToScreen(pt.x + pt.handleOut.x, pt.y + pt.handleOut.y);
          const lineBuilder = new ck.PathBuilder();
          lineBuilder.moveTo(anchor.x, anchor.y);
          lineBuilder.lineTo(hScreen.x, hScreen.y);
          const linePath = lineBuilder.detach();
          canvas.drawPath(linePath, handleLinePaint);
          linePath.delete();
          canvas.drawCircle(hScreen.x, hScreen.y, VECTOR_HANDLE_SIZE / 2, handleFillPaint);
          canvas.drawCircle(hScreen.x, hScreen.y, VECTOR_HANDLE_SIZE / 2, handleStrokePaint);
        }
      }
    }
  }

  handleLinePaint.delete();

  // 3. Draw anchor points (squares: white fill, blue border; selected = blue fill)
  const anchorFillPaint = new ck.Paint();
  anchorFillPaint.setStyle(ck.PaintStyle.Fill);
  anchorFillPaint.setAntiAlias(true);

  const anchorStrokePaint = new ck.Paint();
  anchorStrokePaint.setStyle(ck.PaintStyle.Stroke);
  anchorStrokePaint.setColor(parseColor(ck, SELECTION_COLOR));
  anchorStrokePaint.setStrokeWidth(1);
  anchorStrokePaint.setAntiAlias(true);

  const halfAnchor = VECTOR_ANCHOR_SIZE / 2;

  for (let pi = 0; pi < vectorPaths.length; pi++) {
    const pts = vectorPaths[pi].points;
    for (let i = 0; i < pts.length; i++) {
      const key = `${pi}:${i}`;
      const isSelected = selectedPoints.has(key);
      const screen = ptToScreen(pts[i].x, pts[i].y);

      anchorFillPaint.setColor(
        isSelected ? parseColor(ck, SELECTION_COLOR) : ck.Color(255, 255, 255, 1),
      );

      const rect = ck.LTRBRect(
        screen.x - halfAnchor,
        screen.y - halfAnchor,
        screen.x + halfAnchor,
        screen.y + halfAnchor,
      );
      canvas.drawRect(rect, anchorFillPaint);
      canvas.drawRect(rect, anchorStrokePaint);
    }
  }

  anchorFillPaint.delete();
  anchorStrokePaint.delete();
  handleFillPaint.delete();
  handleStrokePaint.delete();
}

// ─── Pen tool preview ─────────────────────────────────────────────────────

export function drawPenToolPreview(
  ck: CanvasKit,
  canvas: Canvas,
  penPath: VectorPath,
  zoom: number,
  panX: number,
  panY: number,
): void {
  if (penPath.points.length === 0) return;

  // Draw the path so far
  const strokePaint = new ck.Paint();
  strokePaint.setStyle(ck.PaintStyle.Stroke);
  strokePaint.setColor(parseColor(ck, SELECTION_COLOR));
  strokePaint.setStrokeWidth(1.5);
  strokePaint.setAntiAlias(true);

  const pts = penPath.points;
  const builder = new ck.PathBuilder();
  const first = toScreen(pts[0].x, pts[0].y, zoom, panX, panY);
  builder.moveTo(first.x, first.y);

  for (let i = 0; i < pts.length - 1; i++) {
    const curr = pts[i];
    const next = pts[i + 1];
    const hasOut = curr.handleOut && (curr.handleOut.x !== 0 || curr.handleOut.y !== 0);
    const hasIn = next.handleIn && (next.handleIn.x !== 0 || next.handleIn.y !== 0);

    if (hasOut || hasIn) {
      const cp1 = toScreen(
        curr.x + (curr.handleOut?.x ?? 0),
        curr.y + (curr.handleOut?.y ?? 0),
        zoom, panX, panY,
      );
      const cp2 = toScreen(
        next.x + (next.handleIn?.x ?? 0),
        next.y + (next.handleIn?.y ?? 0),
        zoom, panX, panY,
      );
      const end = toScreen(next.x, next.y, zoom, panX, panY);
      builder.cubicTo(cp1.x, cp1.y, cp2.x, cp2.y, end.x, end.y);
    } else {
      const end = toScreen(next.x, next.y, zoom, panX, panY);
      builder.lineTo(end.x, end.y);
    }
  }

  const path = builder.detach();
  canvas.drawPath(path, strokePaint);
  path.delete();
  strokePaint.delete();

  // Draw anchor points
  const fillPaint = new ck.Paint();
  fillPaint.setStyle(ck.PaintStyle.Fill);
  fillPaint.setColor(ck.Color(255, 255, 255, 1));
  fillPaint.setAntiAlias(true);

  const borderPaint = new ck.Paint();
  borderPaint.setStyle(ck.PaintStyle.Stroke);
  borderPaint.setColor(parseColor(ck, SELECTION_COLOR));
  borderPaint.setStrokeWidth(1);
  borderPaint.setAntiAlias(true);

  const halfSize = 4;
  for (let i = 0; i < pts.length; i++) {
    const screen = toScreen(pts[i].x, pts[i].y, zoom, panX, panY);
    // First point: draw larger circle (close target)
    if (i === 0 && pts.length >= 3) {
      canvas.drawCircle(screen.x, screen.y, 5, fillPaint);
      canvas.drawCircle(screen.x, screen.y, 5, borderPaint);
    } else {
      const rect = ck.LTRBRect(
        screen.x - halfSize, screen.y - halfSize,
        screen.x + halfSize, screen.y + halfSize,
      );
      canvas.drawRect(rect, fillPaint);
      canvas.drawRect(rect, borderPaint);
    }

    // Draw handles for the last point if dragging
    if (i === pts.length - 1 && pts[i].handleOut) {
      const ho = pts[i].handleOut!;
      if (ho.x !== 0 || ho.y !== 0) {
        const hScreen = toScreen(pts[i].x + ho.x, pts[i].y + ho.y, zoom, panX, panY);
        const handleLinePaint = new ck.Paint();
        handleLinePaint.setStyle(ck.PaintStyle.Stroke);
        handleLinePaint.setColor(parseColor(ck, '#999999'));
        handleLinePaint.setStrokeWidth(1);
        handleLinePaint.setAntiAlias(true);

        const lb = new ck.PathBuilder();
        lb.moveTo(screen.x, screen.y);
        lb.lineTo(hScreen.x, hScreen.y);
        const lp = lb.detach();
        canvas.drawPath(lp, handleLinePaint);
        lp.delete();
        handleLinePaint.delete();

        canvas.drawCircle(hScreen.x, hScreen.y, 3, fillPaint);
        canvas.drawCircle(hScreen.x, hScreen.y, 3, borderPaint);

        // Also draw the mirrored handleIn
        if (pts[i].handleIn) {
          const hi = pts[i].handleIn!;
          const hiScreen = toScreen(pts[i].x + hi.x, pts[i].y + hi.y, zoom, panX, panY);
          const hlp = new ck.Paint();
          hlp.setStyle(ck.PaintStyle.Stroke);
          hlp.setColor(parseColor(ck, '#999999'));
          hlp.setStrokeWidth(1);
          hlp.setAntiAlias(true);

          const lb2 = new ck.PathBuilder();
          lb2.moveTo(screen.x, screen.y);
          lb2.lineTo(hiScreen.x, hiScreen.y);
          const lp2 = lb2.detach();
          canvas.drawPath(lp2, hlp);
          lp2.delete();
          hlp.delete();

          canvas.drawCircle(hiScreen.x, hiScreen.y, 3, fillPaint);
          canvas.drawCircle(hiScreen.x, hiScreen.y, 3, borderPaint);
        }
      }
    }
  }

  fillPaint.delete();
  borderPaint.delete();
}

// ─── Freehand preview ─────────────────────────────────────────────────────

export function drawFreehandPreview(
  ck: CanvasKit,
  canvas: Canvas,
  points: { x: number; y: number }[],
  zoom: number,
  panX: number,
  panY: number,
): void {
  if (points.length < 2) return;

  const paint = new ck.Paint();
  paint.setStyle(ck.PaintStyle.Stroke);
  paint.setColor(parseColor(ck, SELECTION_COLOR));
  paint.setStrokeWidth(2);
  paint.setAntiAlias(true);

  const builder = new ck.PathBuilder();
  const first = toScreen(points[0].x, points[0].y, zoom, panX, panY);
  builder.moveTo(first.x, first.y);

  for (let i = 1; i < points.length; i++) {
    const p = toScreen(points[i].x, points[i].y, zoom, panX, panY);
    builder.lineTo(p.x, p.y);
  }

  const path = builder.detach();
  canvas.drawPath(path, paint);
  path.delete();
  paint.delete();
}

// ─── Marquee rectangle ────────────────────────────────────────────────────

export function drawMarquee(
  ck: CanvasKit,
  canvas: Canvas,
  rect: { x: number; y: number; w: number; h: number },
  zoom: number,
  panX: number,
  panY: number,
): void {
  // Normalize negative width/height
  const x = rect.w < 0 ? rect.x + rect.w : rect.x;
  const y = rect.h < 0 ? rect.y + rect.h : rect.y;
  const w = Math.abs(rect.w);
  const h = Math.abs(rect.h);

  const topLeft = toScreen(x, y, zoom, panX, panY);
  const screenW = w * zoom;
  const screenH = h * zoom;
  const screenRect = ck.LTRBRect(topLeft.x, topLeft.y, topLeft.x + screenW, topLeft.y + screenH);

  // Fill
  const fillPaint = new ck.Paint();
  fillPaint.setStyle(ck.PaintStyle.Fill);
  fillPaint.setColor(parseColor(ck, MARQUEE_FILL));
  fillPaint.setAntiAlias(true);
  canvas.drawRect(screenRect, fillPaint);
  fillPaint.delete();

  // Stroke
  const strokePaint = new ck.Paint();
  strokePaint.setStyle(ck.PaintStyle.Stroke);
  strokePaint.setColor(parseColor(ck, MARQUEE_STROKE));
  strokePaint.setStrokeWidth(1);
  strokePaint.setAntiAlias(true);
  canvas.drawRect(screenRect, strokePaint);
  strokePaint.delete();
}
